import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Building2, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MapPin, 
  Phone, 
  Mail, 
  Users,
  CheckCircle2,
  XCircle,
  Loader2,
  X,
  Store
} from 'lucide-react';

interface Branch {
  id: number;
  name: string;
  code: string;
  address?: string;
  phone?: string;
  email?: string;
  managerName?: string;
  isActive: boolean;
  createdAt: string;
}

export default function BranchesPage() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    address: '',
    phone: '',
    email: '',
    managerName: '',
    isActive: true
  });

  useEffect(() => {
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/branches');
      if (res.data?.success) {
        setBranches(Array.isArray(res.data.data) ? res.data.data : []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingBranch(null);
    setForm({
      name: '',
      code: '',
      address: '',
      phone: '',
      email: '',
      managerName: '',
      isActive: true
    });
    setShowModal(true);
  };

  const openEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setForm({
      name: branch.name || '',
      code: branch.code || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      managerName: branch.managerName || '',
      isActive: branch.isActive
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await api.put(`/api/branches/${editingBranch.id}`, form);
      } else {
        await api.post('/api/branches', form);
      }
      setShowModal(false);
      loadBranches();
    } catch (err: any) {
      alert(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الفرع؟')) return;
    try {
      await api.delete(`/api/branches/${id}`);
      loadBranches();
    } catch (err: any) {
      alert(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const filtered = branches.filter(b =>
    (b.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.code || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.address || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeCount = branches.filter(b => b.isActive).length;
  const inactiveCount = branches.filter(b => !b.isActive).length;

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition';

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{'الفروع'}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{'إدارة فروع الشركة'}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-blue-500 to-indigo-600 hover:from-indigo-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{'فرع جديد'}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">{'إجمالي الفروع'}</p>
            <p className="text-2xl font-bold text-gray-900">{branches.length}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">{'الفروع النشطة'}</p>
            <p className="text-2xl font-bold text-green-700">{activeCount}</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-sm text-gray-500">{'الفروع غير النشطة'}</p>
            <p className="text-2xl font-bold text-red-700">{inactiveCount}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={'بحث باسم الفرع، الكود، أو العنوان...'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition"
            />
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
              <p className="text-gray-500 mt-4">{'جاري التحميل...'}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{'لا توجد فروع'}</h3>
              <p className="text-gray-500">{'ابدأ بإضافة فرع جديد'}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'الفرع'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'الكود'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'العنوان'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'التواصل'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'المدير'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'الحالة'}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{'الإجراءات'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((branch) => (
                    <tr key={branch.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
                            <Store className="w-5 h-5" />
                          </div>
                          <p className="font-semibold text-gray-900">{branch.name}</p>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-semibold font-mono">
                          {branch.code}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-700 text-sm">{branch.address || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="space-y-1">
                          {branch.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-700 text-sm font-mono">{branch.phone}</span>
                            </div>
                          )}
                          {branch.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-700 text-sm">{branch.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-700 text-sm">{branch.managerName || '-'}</span>
                        </div>
                      </td>
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          branch.isActive 
                            ? 'bg-green-50 text-green-700' 
                            : 'bg-red-50 text-red-700'
                        }`}>
                          {branch.isActive ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openEdit(branch)}
                            className="p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition"
                            title={'تعديل'}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(branch.id)}
                            className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition"
                            title={'حذف'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  {editingBranch ? 'تعديل الفرع' : 'إضافة فرع جديد'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{'اسم الفرع *'}</Label>
                    <Input
                      value={form.name}
                      onChange={(e: any) => setForm({...form, name: e.target.value})}
                      required
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <Label>{'الكود *'}</Label>
                    <Input
                      value={form.code}
                      onChange={(e: any) => setForm({...form, code: e.target.value})}
                      required
                      className={inputClass}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{'العنوان'}</Label>
                    <textarea
                      value={form.address}
                      onChange={(e: any) => setForm({...form, address: e.target.value})}
                      className={inputClass}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>{'رقم الهاتف'}</Label>
                    <Input
                      value={form.phone}
                      onChange={(e: any) => setForm({...form, phone: e.target.value})}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <Label>{'البريد الإلكتروني'}</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e: any) => setForm({...form, email: e.target.value})}
                      className={inputClass}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>{'اسم المدير'}</Label>
                    <Input
                      value={form.managerName}
                      onChange={(e: any) => setForm({...form, managerName: e.target.value})}
                      className={inputClass}
                    />
                  </div>
                  <div className="md:col-span-2 flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={form.isActive}
                      onChange={(e) => setForm({...form, isActive: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="isActive" className="text-sm font-medium">
                      {'نشط'}
                    </label>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1"
                  >
                    {'إلغاء'}
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {editingBranch ? 'تحديث' : 'إضافة'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
